import { env } from "./env";
import { supabase } from "./supabase";

export type ItemStatus = "generating" | "ready" | "failed" | "unresolved";

export interface LibraryItem {
  id: string;
  title: string;
  prompt: string;
  duration_seconds: number;
  loop: boolean;
  status: ItemStatus;
  error_code: string | null;
  engine: string;
  provider: string;
  model_id: string | null;
  byte_size: number | null;
  /** Allowance units reserved for this attempt: an estimate for limits, not the provider's bill. */
  generation_credits: number | null;
  saved: boolean;
  saved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted: boolean;
  provider_called: boolean;
  /** A replay of the same attempt id resumes this row at no extra charge. */
  resumable: boolean;
}

export interface GenerationAccess {
  enabled: boolean;
  allowed: boolean;
  max_seconds: number;
  /** Rolling 24 h reservation allowance in estimate units. */
  daily_credit_limit: number;
  /** Estimate units reserved per requested second. */
  credits_per_second: number;
}

export type IntegrationStatus = "not_linked" | "linked" | "not_implemented";

export interface SonosHousehold {
  household_id: string;
  linked_at: string | null;
  last_used_at: string | null;
}

export interface Me {
  id: string;
  email: string | null;
  generation: GenerationAccess;
  integrations: Record<string, { status: IntegrationStatus; implemented: boolean; households?: SonosHousehold[] }>;
}

export interface DealerClient {
  id: string;
  name: string;
  type: string | null;
  created_at: string | null;
}

export interface DealerMe {
  id: string;
  email: string | null;
  role: "owner" | "staff";
  integrator: { id: string; name: string; email: string };
  clients: DealerClient[];
  /** True only for a platform_admins row: this ID may approve dealer applications. */
  admin?: boolean;
}

export type ApplicationStatus = "pending" | "approved" | "declined";

export interface DealerApplication {
  id: string;
  email: string;
  company: string;
  contact_name: string | null;
  message: string | null;
  status: ApplicationStatus;
  created_at: string | null;
  reviewed_at: string | null;
  /** Whether a SoundLayer ID with the application's email exists yet. */
  signed_up: boolean;
}

export interface ApprovalResult {
  id: string;
  status: ApplicationStatus;
  user_id?: string;
  integrator?: { id: string; name: string };
  generation_access?: boolean;
  /**
   * The server's global generation switch as it stood when the decision was
   * recorded. Absent on older APIs, which is not the same as `false`: then
   * availability is unknown and must not be promised either way.
   */
  generation_enabled?: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Stable server error code when the API sent one (e.g. "daily_limit"). */
    public code: string | null = null,
  ) {
    super(message);
  }
  /** The server answered. A retry is a new attempt; nothing is in flight. */
  get answered(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

async function accessToken(): Promise<string> {
  if (!supabase) throw new ApiError(0, "Sign-in is not configured.");
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError(401, "You are signed out.");
  return token;
}

async function request<T>(path: string, init: RequestInit = {}, extraHeaders: Record<string, string> = {}): Promise<T> {
  const token = await accessToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...extraHeaders,
  };
  if (init.body) headers["Content-Type"] = "application/json";
  const response = await fetch(`${env.apiBase}${path}`, { ...init, headers });
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!response.ok) {
    let message = response.statusText || `HTTP ${response.status}`;
    let code: string | null = null;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (detail && typeof detail === "object" && "message" in detail) {
        message = String((detail as { message: unknown }).message);
        code = "code" in detail ? String((detail as { code: unknown }).code) : null;
      } else {
        message = String(detail);
      }
    }
    throw new ApiError(response.status, message, code);
  }
  return body as T;
}

export const api = {
  me: () => request<Me>("/api/v1/me"),
  /** 403 with code "not_a_dealer" for a client account. */
  dealerMe: () => request<DealerMe>("/api/v1/dealer/me"),
  /** 403 "not_an_admin" unless the signed-in ID is a platform admin. */
  adminApplications: () =>
    request<{ applications: DealerApplication[] }>("/api/v1/dealer/admin/applications").then((r) => r.applications),
  /**
   * 409 "applicant_not_signed_up" when no SoundLayer ID has the application's
   * email yet, "not_pending" when it was already decided, "business_ambiguous"
   * when several business records share the email, or "dealer_elsewhere" when
   * the applicant already belongs to another business. A failure that never
   * reached us (status 0, a dropped connection) is not proof the server did
   * nothing: reconcile by listing the applications again, never by resending.
   */
  approveApplication: (id: string) =>
    request<ApprovalResult>(`/api/v1/dealer/admin/applications/${id}/approve`, { method: "POST" }),
  /** Same 409 codes and the same lost-answer caveat as approveApplication. */
  declineApplication: (id: string) =>
    request<ApprovalResult>(`/api/v1/dealer/admin/applications/${id}/decline`, { method: "POST" }),
  listLibrary: (includeUnsaved = false) =>
    request<{ items: LibraryItem[] }>(`/api/v1/library${includeUnsaved ? "?include=unsaved" : ""}`).then(
      (r) => r.items,
    ),
  getItem: (id: string) => request<LibraryItem>(`/api/v1/library/${id}`),
  getAttempt: (key: string) => request<LibraryItem>(`/api/v1/library/attempts/${encodeURIComponent(key)}`),
  generate: (
    body: { prompt: string; duration_seconds: number; loop: boolean; title?: string },
    idempotencyKey: string,
  ) =>
    request<LibraryItem>(
      "/api/v1/library/generate",
      { method: "POST", body: JSON.stringify(body) },
      { "Idempotency-Key": idempotencyKey },
    ),
  save: (id: string, title: string) =>
    request<LibraryItem>(`/api/v1/library/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),
  remove: (id: string) => request<void>(`/api/v1/library/${id}`, { method: "DELETE" }),
  /**
   * A short-lived, owner-only signed URL plus its lifetime in seconds. The URL
   * is a credential: it goes to the <audio> element and nowhere else, never to
   * a log, storage or error text.
   */
  audioUrl: (id: string) => request<{ url: string; expires_in: number }>(`/api/v1/library/${id}/audio`),
  linkSonos: (code: string) =>
    request<{ household_id: string; status: string }>("/api/v1/me/sonos/link", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  unlinkSonos: () => request<void>("/api/v1/me/sonos", { method: "DELETE" }),
  health: async () => {
    const response = await fetch(`${env.apiBase}/health`);
    return { status: response.status, body: (await response.json()) as Record<string, string> };
  },
};

/** Human wording for the server's stable error codes. */
export function describeError(code: string | null | undefined): string {
  switch (code) {
    case "provider_auth":
      return "The audio provider rejected SoundLayer's credentials. Nothing was charged. The operator needs to check the provider key.";
    case "provider_quota":
      return "The audio provider reports the account is out of credits. Nothing new was generated.";
    case "provider_rate_limit":
      return "The audio provider is rate limiting requests. Wait a minute and try again.";
    case "provider_rejected":
      return "The audio provider rejected this description. Try rewording it.";
    case "provider_unreachable":
      return "SoundLayer could not reach the audio provider. Try again shortly.";
    case "not_configured":
      return "Generation is not available yet.";
    case "storage_error":
      return "The sound was generated but could not be stored, so it is not in your library. This attempt may have used credits.";
    case "outcome_unknown":
      return "SoundLayer sent the request but never received the result. It may have used credits. It will not be retried on its own.";
    case "provider_server_error":
      return "The audio provider returned a server error after the request was sent. It may have used credits. It will not be retried on its own.";
    case "provider_empty_response":
      return "The audio provider accepted the request but returned no audio. It may have used credits. It will not be retried on its own.";
    case "resume_deferred":
      return "Your earlier attempt is waiting for a free slot or allowance and will resume when you check again.";
    case "invalid_request":
      return "The request was rejected before anything was sent.";
    case "generation_disabled":
      return "Generation is switched off right now.";
    case "generation_not_allowed":
      return "Generation is not switched on for this SoundLayer ID yet.";
    case "concurrent_limit":
      return "A sound is already being generated for this account. Wait for it to finish.";
    case "daily_limit":
      return "This account has reached its generation allowance for today. The allowance counts estimated reservations, not confirmed provider billing.";
    case "service_limit":
      return "SoundLayer's generation allowance is used up for now. Try again later.";
    default:
      return "Generation failed for an unexpected reason. Nothing was added to your library.";
  }
}
